/**
 * How this platform writes a date, in one place.
 *
 * Dates were formatted ad hoc wherever one appeared, so the same record
 * could read `3 Feb 2026` in a list, `03-02-2026` in a drawer and
 * `3/2/2026` in an export. Lifeblood 3.13 asks for one shape everywhere —
 * `DD/MM/YYYY`, zero-padded, so a date cannot be read as another date —
 * and that promise can only be kept by one definition the app, the mobile
 * client and the gateway's output binder all compile.
 *
 * Patterns are the tokens `formatDateTokens` understands, deliberately not
 * date-fns: this module is shared with the gateway and the PDF binder,
 * which have no date-fns, and the tokens already carry a time zone.
 *
 * ZONES. A timestamp has no day of its own. `2026-02-03T13:30:00Z` is the
 * 3rd in Melbourne and the 4th in Auckland, and formatting without a zone
 * silently uses whichever machine happens to be rendering — a reviewer's
 * laptop, or a PDF worker in UTC. Every function here takes the zone it
 * should be read in, and callers are expected to pass the tenant's, so a
 * record says the same day to everyone who opens it.
 *
 * These are DISPLAY patterns. A form element with its own `dateFormat`
 * setting keeps it (see resolveDatePattern) — that is the form author's
 * choice about their own question, not the platform's about its chrome.
 *
 * TENANT CHOICE. The constants are the platform's defaults. A workspace
 * can pick another shape from the preset lists below through the unified
 * settings (`display.date_format`, `display.time_format`), and every
 * consumer passes the resolved choice in as `datePattern` / `timePattern`.
 * The lists are the setting's enum options, so ISM's definition
 * (`DisplayFormatSettings.php`) and this file must agree; each side has a
 * test pinning its copy.
 */
import { formatDateTokens, parseDateValue } from "./renderEntityReport";

/** The platform's date, zero-padded: 03/02/2026. */
export const DISPLAY_DATE_PATTERN = "DD/MM/YYYY";

/** The same date with a wall-clock time: 03/02/2026 1:30 pm. */
export const DISPLAY_DATE_TIME_PATTERN = "DD/MM/YYYY h:mm a";

/** Just the time: 1:30 pm. */
export const DISPLAY_TIME_PATTERN = "h:mm a";

/**
 * CHROME shapes: a day without its year (3 Feb), a month (Feb 2026), a
 * weekday with its day (Mon 3 Feb). These label calendars, charts and
 * groupings where the year is given by the surrounding view. They spell
 * the month, so they cannot be read as another date, and they do not
 * follow the workspace's numeric shape: that shape is for a full date.
 */
export const DISPLAY_DAY_MONTH_PATTERN = "D MMM";
export const DISPLAY_MONTH_YEAR_PATTERN = "MMM YYYY";
export const DISPLAY_WEEKDAY_DAY_MONTH_PATTERN = "ddd D MMM";

/**
 * The date shapes a workspace may choose between. The stored setting value
 * IS the pattern, so a reader of the database and a reader of this list
 * see the same thing. Ordered as the picker shows them.
 */
export const DISPLAY_DATE_PATTERN_OPTIONS: readonly string[] = [
  "DD/MM/YYYY",
  "MM/DD/YYYY",
  "YYYY-MM-DD",
  "D MMM YYYY",
];

/** The time shapes a workspace may choose between. */
export const DISPLAY_TIME_PATTERN_OPTIONS: readonly string[] = [
  "h:mm a",
  "HH:mm",
];

/** The unified-settings keys the two choices are stored under. */
export const DISPLAY_FORMAT_SETTING_KEYS = {
  date: "display.date_format",
  time: "display.time_format",
} as const;

export interface DisplayFormatOptions {
  /**
   * The zone the date should be read in — the tenant's, normally. Omitted
   * means the runtime's, which is right only for something that is about
   * the viewer rather than the record.
   */
  timeZone?: string | null;
  /** What to render when there is no date. Defaults to an em dash. */
  fallback?: string;
  /**
   * The workspace's chosen date shape, one of DISPLAY_DATE_PATTERN_OPTIONS.
   * Null or omitted means the platform default.
   */
  datePattern?: string | null;
  /** The workspace's chosen time shape, one of DISPLAY_TIME_PATTERN_OPTIONS. */
  timePattern?: string | null;
  /**
   * Lead a full date with its weekday: `Tue 03/02/2026`. For the places
   * that always said what day of the week it was (a scheduled start, a
   * roster), where the weekday is the point and the date the detail.
   */
  weekday?: boolean;
}

/** The date pattern an options object resolves to. */
export function resolveDisplayDatePattern(
  options: DisplayFormatOptions = {}
): string {
  return options.datePattern || DISPLAY_DATE_PATTERN;
}

/** The time pattern an options object resolves to. */
export function resolveDisplayTimePattern(
  options: DisplayFormatOptions = {}
): string {
  return options.timePattern || DISPLAY_TIME_PATTERN;
}

/**
 * Anything a date arrives as on the way to a screen: an ISO or bridge
 * string from the API, a Date, a millisecond epoch, a moment or dayjs
 * object (anything with `toDate()`), or nothing.
 */
export type DisplayDateInput =
  | string
  | number
  | Date
  | { toDate(): Date }
  | null
  | undefined;

/**
 * A calendar day on the wire: `2026-02-03`, nothing else. It has no
 * time and no zone, so the only honest rendering is that day, wherever
 * it is read. Parsing it as UTC midnight (which `new Date` does) and
 * then reading it in a zone west of UTC would land it on the 2nd.
 */
const CALENDAR_DAY = /^\d{4}-\d{2}-\d{2}$/;

export function isCalendarDayInput(value: DisplayDateInput): boolean {
  return typeof value === "string" && CALENDAR_DAY.test(value.trim());
}

/** Narrow an input to a usable Date, or null if it cannot be read. */
export function toDisplayDate(value: DisplayDateInput): Date | null {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === "object") {
    const date = value.toDate();
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
  }
  const raw = value.trim();
  if (CALENDAR_DAY.test(raw)) {
    // Local midnight on purpose, see CALENDAR_DAY.
    const [y, m, d] = raw.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  // The bridge writes `YYYY-MM-DD HH:mm:ss`, which Safari refuses as is.
  return parseDateValue(raw);
}

/** The zone a value should be read in: none for a calendar day. */
function zoneFor(
  value: DisplayDateInput,
  options: DisplayFormatOptions
): string | null | undefined {
  return isCalendarDayInput(value) ? null : options.timeZone;
}

/** The chosen date pattern, led by the weekday when asked. */
function datePatternFor(options: DisplayFormatOptions): string {
  const pattern = resolveDisplayDatePattern(options);
  return options.weekday ? `ddd ${pattern}` : pattern;
}

/** `03/02/2026`, in the given zone and the given (or default) shape. */
export function formatDisplayDate(
  value: DisplayDateInput,
  options: DisplayFormatOptions = {}
): string {
  const date = toDisplayDate(value);
  if (!date) return options.fallback ?? "—";
  return formatDateTokens(
    date,
    datePatternFor(options),
    zoneFor(value, options)
  );
}

/** `03/02/2026 1:30 pm`: the date shape, a space, the time shape. */
export function formatDisplayDateTime(
  value: DisplayDateInput,
  options: DisplayFormatOptions = {}
): string {
  const date = toDisplayDate(value);
  if (!date) return options.fallback ?? "—";
  return formatDateTokens(
    date,
    `${datePatternFor(options)} ${resolveDisplayTimePattern(options)}`,
    zoneFor(value, options)
  );
}

/** `1:30 pm`, in the given zone and the given (or default) shape. */
export function formatDisplayTime(
  value: DisplayDateInput,
  options: DisplayFormatOptions = {}
): string {
  const date = toDisplayDate(value);
  if (!date) return options.fallback ?? "—";
  return formatDateTokens(
    date,
    resolveDisplayTimePattern(options),
    zoneFor(value, options)
  );
}

/** `3 Feb`: a day labelled without its year. */
export function formatDisplayDayMonth(
  value: DisplayDateInput,
  options: DisplayFormatOptions = {}
): string {
  const date = toDisplayDate(value);
  if (!date) return options.fallback ?? "—";
  return formatDateTokens(
    date,
    DISPLAY_DAY_MONTH_PATTERN,
    zoneFor(value, options)
  );
}

/** `Feb 2026`: a month. */
export function formatDisplayMonthYear(
  value: DisplayDateInput,
  options: DisplayFormatOptions = {}
): string {
  const date = toDisplayDate(value);
  if (!date) return options.fallback ?? "—";
  return formatDateTokens(
    date,
    DISPLAY_MONTH_YEAR_PATTERN,
    zoneFor(value, options)
  );
}

/** `Mon 3 Feb`: a weekday with its day, for calendar chrome. */
export function formatDisplayWeekdayDate(
  value: DisplayDateInput,
  options: DisplayFormatOptions = {}
): string {
  const date = toDisplayDate(value);
  if (!date) return options.fallback ?? "—";
  return formatDateTokens(
    date,
    DISPLAY_WEEKDAY_DAY_MONTH_PATTERN,
    zoneFor(value, options)
  );
}
